<script lang="ts" setup>
// -----------------------
// props & emits
// -----------------------
const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  min: {
    type: Number,
    default: () => 1,
  },
  max: {
    type: Number,
    default: () => 999,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

// -----------------------
// computed properties
// -----------------------
const isRemoveButtonDisabled = computed(() => {
  return value.value <= props.min || props.max === 0 || props.disabled;
});

const isAddButtonDisabled = computed(() => {
  return value.value >= props.max || props.max === 0 || props.disabled;
});

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    // Validate and clamp the value before emitting
    let clampedValue = value;

    if (clampedValue > props.max) {
      clampedValue = props.max;
    } else if (clampedValue < props.min) {
      clampedValue = props.min;
    }

    emit("update:modelValue", clampedValue);
  },
});

// Handle direct input validation
const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let inputValue = parseInt(input.value);

  if (isNaN(inputValue)) {
    inputValue = props.min;
  }

  // Clamp the value immediately
  if (inputValue > props.max) {
    inputValue = props.max;
    input.value = props.max.toString();
  } else if (inputValue < props.min) {
    inputValue = props.min;
    input.value = props.min.toString();
  }

  // Update the value through the computed setter
  value.value = inputValue;
};

watch(
  () => props.max,
  (newValue) => {
    if (newValue === 0) {
      value.value = props.min;
    } else if (newValue < value.value) {
      value.value = newValue;
    }
  },
);
</script>

<template>
  <div class="flex rounded-[3px]">
    <button
      :disabled="isRemoveButtonDisabled"
      class="flex leading-[26px] justify-center border border-brand-primary rounded-l-[3px] font-thin text-brand-headline text-[20px]"
      :class="{ 'text-brand-headline/30': isRemoveButtonDisabled }"
      @click="value--"
    >
      -
    </button>
    <input
      :value="value"
      type="number"
      :min="min"
      :max="max"
      class="w-[29px] h-[29px] py-[5px] text-center border-y border-brand-primary text-brand-headline text-xs"
      @input="handleInput"
    />
    <button
      :disabled="isAddButtonDisabled"
      class="flex leading-[25px] justify-center border border-brand-primary rounded-r-[3px] font-thin text-brand-headline text-[20px]"
      :class="{ 'text-brand-headline/30': isAddButtonDisabled }"
      @click="value++"
    >
      +
    </button>
  </div>
</template>
<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

button {
  flex: 0 0 29px;
}
</style>
